/**
 * Core API Client
 *
 * Handles financial data retrieval from Core API (TigerBeetle ledger).
 * Non-financial data (case metadata, customer info, etc.) comes from database.
 */

import { getConnectApiBaseUrl } from '@/lib/connect-api';
import type { AccountViewResponse } from '@/types/core-api';

export class CoreApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'CoreApiError';
  }
}

/**
 * Helper function to get authentication headers for core-api requests
 * Supports both Stage 1 (X-Access-Code) and Stage 2 (JWT) authentication
 */
async function getAuthHeaders(authStage: 'limited' | 'full' | null, getAccessToken?: () => Promise<string | null>): Promise<Record<string, string>> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (authStage === 'full' && getAccessToken) {
    // Stage 2: Use JWT Bearer token
    try {
      const token = await getAccessToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        return headers;
      }
    } catch (error) {
      console.error('[core-api] Error getting access token:', error);
      throw new CoreApiError('Failed to get access token', 401, error);
    }
  }

  if (authStage === 'limited') {
    // Stage 1: Use access code from localStorage
    const accessCode = localStorage.getItem('access_code');
    if (accessCode) {
      headers['X-Access-Code'] = accessCode;
      return headers;
    }
  }

  console.error('[core-api] No authentication method available. authStage:', authStage);
  throw new CoreApiError('No authentication found. Please log in again.', 401);
}

/**
 * Fetch account view from core-api.
 *
 * Returns the full AccountViewResponse including the case set with financial
 * data and any active account-scoped payment plan. The account/customer
 * metadata fields are also populated but the customer portal uses the
 * dedicated database-api path for those (see DebtDataContext); this caller
 * only reads case_details and account_payment_plan from the response.
 *
 * @param accountId - Account ID (e.g., 'acct_xxx')
 * @param authStage - Authentication stage ('limited' or 'full')
 * @param getAccessToken - Optional function to get JWT token
 * @returns The full account view response
 */
export async function getCaseData(
  accountId: string,
  authStage: 'limited' | 'full' | null,
  getAccessToken?: () => Promise<string | null>,
): Promise<AccountViewResponse> {
  const params = new URLSearchParams({ exclude_categories: 'closure' });
  const finalUrl = `${getConnectApiBaseUrl()}/connect/v1/accounts/${accountId}/view?${params}`;

  try {
    const headers = await getAuthHeaders(authStage, getAccessToken);

    const response = await fetch(finalUrl, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: response.statusText }));
      console.error('[core-api] Error response:', errorData);
      const errorMessage = errorData.detail || errorData.message || `Core API error: ${response.status}`;
      throw new CoreApiError(errorMessage, response.status, errorData);
    }

    const data = (await response.json()) as AccountViewResponse;
    return data;
  } catch (error) {
    if (error instanceof CoreApiError) {
      throw error;
    }
    console.error('[core-api] Unexpected error:', error);
    throw new CoreApiError(`Failed to fetch case data: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}
